import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useFeatureFlag } from 'contexts';

import { setPhoneNumber } from 'app/domain/authentication/slice';
import {
  addPersonalInformationData,
  updateBravoLeadId,
  updateGroupId,
  updateProfileData,
  updateProfileImage,
} from 'app/domain/profile/slice';
import selectProfileState from 'app/domain/profile/slice/selectors';
import useGetApplicationDetail from 'app/pages/VehicleAndLoanReview/hooks/useGetApplicationDetail';
import { RoutePath } from 'app/Routing/constants';

import selectLoanState from './slice/selectors';
import { ApplicationStatus } from './constants';
import {
  confirmApplicationSubmission,
  confirmKTP,
  confirmKTPUploaded,
  confirmLoanAndVehicle,
  confirmOfflineTouchPoint,
  confirmPersonalInformation,
  confirmSelfie,
  setFromFinalEdit,
  setIsUpdateToVehicleInfoProvided,
  setIsUpdateToVerified,
  updateSkipKTP,
  updateSkipLiveness,
} from './slice';

export default function SequentialPage({ children }: any) {
  const location = useLocation();
  const {
    redirect: {
      isKTPVerified,
      isLoanAndVehicleConfirmed,
      isKTPUploaded,
      isSelfieVerified,
      isAdditionalPersonalInformationGiven,
      isOfflineTouchPointConfirmed,
      isApplicationSubmitted,
      isFromFinalEdit,
    },
    skip_liveness,
    is_update_to_verified,
    is_update_to_vehicle_info_provided,
  } = useSelector(selectLoanState);
  const { isCASUploadKTPEnabled, isLeadReviveEnabled } = useFeatureFlag();
  const { applicationId, isOpenGeoplaceScreen, isDigitalApplication } =
    useSelector(selectProfileState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: applicationData } = useGetApplicationDetail(
    applicationId || '',
  );

  const [searchParams] = useSearchParams();
  const resultSelfie = searchParams.get('result');

  useEffect(() => {
    if (applicationData) {
      const {
        basic_info: {
          education,
          occupation,
          monthly_income,
          home_ownership,
          marriage_type,
          phone_number,
        },
        bravo_lead_id,
        group_id,
        application_status,
        verification_identity,
      } = applicationData;

      const { skip_ktp, skip_liveness, selfie_image, ktp } =
        verification_identity;

      dispatch(updateSkipKTP(!!skip_ktp));
      dispatch(updateSkipLiveness(!!skip_liveness));

      // update profileData/personalInformationData from redux persist
      if (!isDigitalApplication && !is_update_to_verified) {
        dispatch(updateProfileData(verification_identity));
      }
      dispatch(
        addPersonalInformationData({
          highest_education: education,
          occupation,
          monthly_income:
            (monthly_income !== 0 && monthly_income.toString()) || '',
          residence_status: home_ownership,
          marital_status: marriage_type,
          spouse_birth_date: applicationData?.spouse_identity?.birth_date || '',
          spouse_birth_place:
            applicationData?.spouse_identity?.birth_place || '',
          spouse_id_number: applicationData?.spouse_identity?.id_number || '',
          spouse_full_name: applicationData?.spouse_identity?.full_name || '',
        }),
      );

      if (bravo_lead_id) {
        dispatch(updateBravoLeadId(bravo_lead_id));
      }
      if (group_id) {
        dispatch(updateGroupId(group_id));
      }
      if (phone_number) {
        dispatch(setPhoneNumber(phone_number));
      }
      switch (application_status) {
        case ApplicationStatus.LEAD_PENDING:
          if (isDigitalApplication)
            navigate(RoutePath.vehicleDetail, { replace: true });
          break;
        case ApplicationStatus.VERIFIED:
          dispatch(setIsUpdateToVerified(true));
          break;
        case ApplicationStatus.VEHICLE_INFO_PROVIDED:
          if (!isOpenGeoplaceScreen) {
            dispatch(setIsUpdateToVehicleInfoProvided(true));
            dispatch(confirmLoanAndVehicle());
            navigate(
              {
                pathname: RoutePath.verifyIdentityPage,
                search: resultSelfie ? '?result=success' : '',
              },
              { replace: true },
            );
            break;
          }
          break;
        case ApplicationStatus.BASIC_INFO_PROVIDED:
          dispatch(confirmLoanAndVehicle());
          if (!ktp || !selfie_image) {
            navigate(
              {
                pathname: RoutePath.verifyIdentityPage,
                search: resultSelfie ? '?result=success' : '',
              },
              { replace: true },
            );
          } else {
            dispatch(
              updateProfileImage({
                selfie_image: selfie_image,
                ktp: ktp,
              }),
            );
            dispatch(confirmSelfie());
            dispatch(confirmKTPUploaded());
            dispatch(confirmKTP());
            navigate(RoutePath.personalInformation, { replace: true });
          }
          break;
        case ApplicationStatus.COMPLETED_INFO:
          dispatch(confirmLoanAndVehicle());
          if (ktp) {
            dispatch(confirmKTPUploaded());
            dispatch(confirmKTP());
          }

          /*
            redirection to verify identity screen cases:
            - ktp is empty
            - when REACT_APP_CAS_UPLOAD_KTP is off, ktp / selfie is empty (skip_liveness is not relevant)
            - when REACT_APP_CAS_UPLOAD_KTP is on, ktp is empty / skip_liveness is false and selfie is empty
          */
          if (
            !ktp ||
            (!isCASUploadKTPEnabled && !selfie_image) ||
            (isCASUploadKTPEnabled && !skip_liveness && !selfie_image)
          ) {
            if (ktp && !resultSelfie) {
              dispatch(setFromFinalEdit(RoutePath.reviewApplication));
              navigate(RoutePath.takeSelfieOnly, { replace: true });
              break;
            }
            navigate(
              {
                pathname: RoutePath.verifyIdentityPage,
                search: resultSelfie ? '?result=success' : '',
              },
              { replace: true },
            );
          } else {
            dispatch(confirmSelfie());
            dispatch(confirmPersonalInformation());
            dispatch(confirmOfflineTouchPoint());
            if (!isFromFinalEdit.length)
              navigate(RoutePath.reviewApplication, { replace: true });
          }
          break;
        case ApplicationStatus.USER_CONFIRMED:
          dispatch(confirmLoanAndVehicle());
          dispatch(confirmSelfie());
          dispatch(confirmKTPUploaded());
          dispatch(confirmKTP());
          dispatch(confirmPersonalInformation());
          dispatch(confirmApplicationSubmission());
          dispatch(confirmOfflineTouchPoint());
          navigate(RoutePath.applicationSubmission, { replace: true });
          break;
        case ApplicationStatus.SURVEY:
        case ApplicationStatus.APPROVAL_IN_PROGRESS:
        case ApplicationStatus.SUBMITTED_SALESTRAX:
        case ApplicationStatus.APPROVED:
        case ApplicationStatus.REJECTED:
        case ApplicationStatus.REJECTED_BLACKLIST_AREA:
        case ApplicationStatus.CANCEL:
        case ApplicationStatus.GO_LIVE:
        case ApplicationStatus.DONE_GO_LIVE:
        case ApplicationStatus.DISBURSED:
          dispatch(confirmLoanAndVehicle());
          dispatch(confirmSelfie());
          dispatch(confirmKTPUploaded());
          dispatch(confirmKTP());
          dispatch(confirmPersonalInformation());
          dispatch(confirmOfflineTouchPoint());
          dispatch(confirmApplicationSubmission());
          navigate(RoutePath.approvalInformation, { replace: true });
          break;
        case ApplicationStatus.LEAD_EXPIRED:
          if (isLeadReviveEnabled) navigate(RoutePath.leadRevivePage);
          break;
        default:
          dispatch(confirmLoanAndVehicle());
          dispatch(confirmSelfie());
          dispatch(confirmKTPUploaded());
          dispatch(confirmKTP());
          dispatch(confirmPersonalInformation());
          dispatch(confirmOfflineTouchPoint());
          dispatch(confirmApplicationSubmission());
          navigate(RoutePath.applicationSubmission, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData]);

  if (applicationId === null) {
    return <Navigate to={RoutePath.applicationIdErrorPage} replace />;
  }
  if (applicationData) {
    switch (location.pathname) {
      case RoutePath.verifyIdentityPage:
      case RoutePath.faceVerificationPage:
        if (isKTPVerified && !isFromFinalEdit.length)
          navigate(RoutePath.personalInformation, { replace: true });
        else if (!isLoanAndVehicleConfirmed)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.ktpVerificationPage:
        if (isKTPVerified && !isFromFinalEdit.length)
          navigate(RoutePath.personalInformation, { replace: true });
        else if (!isSelfieVerified && !skip_liveness)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.reviewInformation:
        if (isKTPVerified && !isFromFinalEdit.length)
          navigate(RoutePath.personalInformation, { replace: true });
        else if (!isKTPUploaded)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.personalInformation:
        if (isOfflineTouchPointConfirmed && !isFromFinalEdit.length)
          navigate(RoutePath.reviewApplication, { replace: true });
        else if (!isKTPVerified)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.offlineTouchPoint:
        if (!isAdditionalPersonalInformationGiven)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.reviewApplication:
        if (!isOfflineTouchPointConfirmed)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.applicationSubmission:
        if (!isApplicationSubmitted)
          navigate(RoutePath.landingPage, { replace: true });
        break;
      case RoutePath.vehicleDetail:
      case RoutePath.vehicleYear:
      case RoutePath.vehicleBrand:
      case RoutePath.reviewVehicleAndLoan:
      case RoutePath.vehicleVariant:
      case RoutePath.vehicleModel:
      case RoutePath.loanEstimation:
      case RoutePath.loanEstimationInstallmentList:
        if (
          is_update_to_vehicle_info_provided &&
          isLoanAndVehicleConfirmed &&
          isFromFinalEdit.length === 0
        )
          navigate(RoutePath.verifyIdentityPage, { replace: true });
        break;
      default:
        break;
    }
  }

  if (applicationData && children) {
    return children;
  }

  return <Outlet />;
}
